/**
 * The continuity plan behind an entity: where the current plan is, and
 * whether its annual review is due.
 *
 * The plans themselves live outside the repo - the technology one is a Google
 * Doc owned by the VP of Engineering - and a plan's "last updated" is the
 * date in its own version table, which a human knows and nothing here can
 * read. So both are declared on the entity the plan covers, as annotations:
 *
 *   metadata:
 *     annotations:
 *       savr.com/bcm-plan: https://docs.google.com/document/d/...
 *       savr.com/bcm-plan-updated: '2026-09-16' # quoted: a YAML 1.1 parser reads it bare as a timestamp
 *
 * On a BusinessProcess for preference, since that is what already carries the
 * MTPD the plan recovers to, but any entity may carry them.
 *
 * Beside the process model rather than in the frontend because two readers
 * need the same judgement: the card on the entity page, and the backend test
 * over the committed catalog that fails a date nobody could judge a review
 * from. A rule that lived in only one of them would let the other drift.
 */

export const BCM_PLAN_ANNOTATION = 'savr.com/bcm-plan';
export const BCM_PLAN_UPDATED_ANNOTATION = 'savr.com/bcm-plan-updated';

/**
 * The review cycle every plan in the BCM set states: "at least annually, and
 * on material change". One year from the last update is therefore the day the
 * plan falls due, and the day after it is when it is overdue.
 */
const REVIEW_CYCLE_YEARS = 1;

/**
 * Where a plan stands against its review cycle.
 *
 * `unrecorded` is a warning, not a neutral state. A plan with no judgeable
 * date may be a year old or a week old, and nothing here can tell - so it is
 * reported as the gap it is, never read as current. `reason` says which gap:
 * the annotation is missing, is not a calendar day, or names a day that has
 * not happened yet (which a "last updated" cannot, so it is a typo that would
 * otherwise read as current for decades). The last two carry the value as
 * written, so the card can show what is wrong rather than send the reader to
 * find the YAML.
 */
export type PlanReview =
  | { state: 'current'; updated: string; dueBy: string }
  | { state: 'overdue'; updated: string; dueBy: string }
  | { state: 'unrecorded'; reason: 'missing' }
  | { state: 'unrecorded'; reason: 'invalid' | 'future'; value: string };

/**
 * Where the plan is. `invalid` is the annotation set to something that is
 * not an https URL, and the card says so rather than linking it: an
 * annotation is a string anyone with a catalog PR can set, and the catalog
 * data test only covers the processes file, not a System in some service
 * repo that picks the annotation up. `missing` with a date present is a
 * mis-keyed or forgotten link, which is a gap of the same kind.
 */
export type PlanLink =
  | { state: 'ok'; url: string }
  | { state: 'missing' }
  | { state: 'invalid'; value: string };

export type BcmPlan = { link: PlanLink; review: PlanReview };

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A YYYY-MM-DD string as the UTC midnight it names, or null when it is not
 * a real calendar day - "2026-02-30" matches the pattern and is rejected by
 * the round trip.
 */
function parseDay(value: string): Date | null {
  const match = DAY.exec(value);
  if (!match) {
    return null;
  }
  const [, year, month, day] = match.map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return utcDay(parsed) === value ? parsed : null;
}

/** A moment as the YYYY-MM-DD day it falls on in UTC. */
function utcDay(at: Date): string {
  return at.toISOString().slice(0, 10);
}

/**
 * Judge a plan's `savr.com/bcm-plan-updated` value against the review cycle.
 *
 * Judged on the UTC day, not the viewer's: the day a plan falls due must be
 * the same in every browser and in CI, and a review that is overdue in
 * Stockholm but current in a test run would be two answers to one question.
 *
 * The boundary is inclusive at the anniversary: a plan updated on 16
 * September is current through the following 16 September and overdue from
 * the 17th. Updating on 29 February rolls the anniversary to 1 March.
 *
 * The future check tolerates one day. The date is copied from a document by
 * someone on their own calendar, and just after midnight in Stockholm the
 * day they correctly write is still tomorrow in UTC; rejecting it would fail
 * CI for two hours a night and tell viewers a true date had not happened.
 * The day after tomorrow is on nobody's calendar and is refused.
 */
export function planReview(updated: unknown, now: Date): PlanReview {
  if (updated === undefined || updated === null) {
    return { state: 'unrecorded', reason: 'missing' };
  }
  if (typeof updated !== 'string') {
    return { state: 'unrecorded', reason: 'invalid', value: String(updated) };
  }
  const value = updated.trim();
  if (value === '') {
    return { state: 'unrecorded', reason: 'missing' };
  }
  const updatedOn = parseDay(value);
  if (!updatedOn) {
    return { state: 'unrecorded', reason: 'invalid', value };
  }
  const today = utcDay(now);
  const tomorrow = utcDay(new Date(now.getTime() + 24 * 60 * 60 * 1000));
  const updatedDay = utcDay(updatedOn);
  if (updatedDay > tomorrow) {
    return { state: 'unrecorded', reason: 'future', value };
  }
  const dueBy = utcDay(
    new Date(
      Date.UTC(
        updatedOn.getUTCFullYear() + REVIEW_CYCLE_YEARS,
        updatedOn.getUTCMonth(),
        updatedOn.getUTCDate(),
      ),
    ),
  );
  return {
    state: today > dueBy ? 'overdue' : 'current',
    updated: updatedDay,
    dueBy,
  };
}

/**
 * Judge a plan's `savr.com/bcm-plan` value as a link to follow.
 *
 * `ok` needs a parseable https URL, not a prefix: `https://` on its own is
 * not somewhere to send a reader, and `HTTPS://docs...` is. Anything else
 * is `invalid` and carries what was written, so the card can show the typo
 * rather than call it "no plan".
 */
export function planLink(link: unknown): PlanLink {
  if (typeof link !== 'string' || link.trim() === '') {
    return { state: 'missing' };
  }
  const value = link.trim();
  try {
    const url = new URL(value);
    if (url.protocol === 'https:' && url.hostname) {
      return { state: 'ok', url: value };
    }
  } catch {
    // Not a URL at all; reported below.
  }
  return { state: 'invalid', value };
}

/**
 * The plan an entity declares, or null when it declares none.
 *
 * Null only when neither annotation is set. Either one alone is still a
 * plan with a gap - a link with no date, or a date whose link is missing or
 * not a URL - and the card shows the gap, because dropping the half that is
 * there would hide the warning with it. A mis-keyed `savr.com/bcm-plan`
 * beside a real date must not read as "no plan".
 */
export function bcmPlanOf(
  annotations: Record<string, string> | undefined,
  now: Date,
): BcmPlan | null {
  const link = planLink(annotations?.[BCM_PLAN_ANNOTATION]);
  const review = planReview(annotations?.[BCM_PLAN_UPDATED_ANNOTATION], now);
  if (
    link.state === 'missing' &&
    review.state === 'unrecorded' &&
    review.reason === 'missing'
  ) {
    return null;
  }
  return { link, review };
}
